import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import Icon from './Icon';
import Sheet from './Sheet';
import { Group, SectionTitle } from './SettingsRows';
import {
  GoogleWebButton, PROVIDER_LABELS, ProviderTile, providerAvailable, useProviderSignIn,
} from './SocialButtons';
import { useApp } from '../lib/AppContext';

const PROVIDERS = ['zalo', 'google', 'apple'];
const FIELD = 'input h-11 w-full rounded-[14px] border-line text-base';
const SMALL = 'btn h-10 min-h-10 rounded-[12px] border px-4 text-sm font-bold';

// "hoang•••@gmail.com": enough to recognise the account without showing it whole.
const maskEmail = (email) => {
  const [local, domain] = email.split('@');
  return `${local.slice(0, Math.min(5, Math.max(1, local.length - 1)))}•••@${domain}`;
};

// What a provider row says under its name: the account it is connected to.
const accountName = (info) => (info.email ? maskEmail(info.email) : info.name || 'Đã kết nối');

// "Zalo", "Zalo hoặc số điện thoại": the ways in that stay after removing `except`.
const remainingNames = (security, except) => security.sign_in_methods.filter((m) => m !== except).map((m) => (
  m === 'phone' ? `số điện thoại ${security.phone}` : m === 'password' ? `email ${maskEmail(security.email)}` : PROVIDER_LABELS[m]
)).join(' hoặc ');

// "số điện thoại 0846… hoặc email a@gmail.com": what the password logs in with.
const loginNames = (security, email = security?.email) => [
  security?.phone && `số điện thoại ${security.phone}`, email && `email ${email}`,
].filter(Boolean).join(' hoặc ');

// Optional password: it logs in with the phone number (accounts from before) and/or the email of a
// connected Google or Apple account (the app can't send email, so it only uses emails those providers verified).
function PasswordSheet({ open, security, onClose, onSaved }) {
  const { api } = useApp();
  const [values, setValues] = useState({ password: '', password_confirmation: '', email: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const emails = security?.provider_emails || [];

  useEffect(() => {
    if (open) setValues({ password: '', password_confirmation: '', email: security?.email || emails[0] || '' });
    setError(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSaved(await api.patch('/account/password', values));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title={security?.has_password ? 'Đổi mật khẩu' : 'Đặt mật khẩu'}>
      {emails.length === 0 && !security?.email && !security?.phone
        ? <p className="text-sm">Mật khẩu dùng để đăng nhập bằng email. Hãy kết nối Google hoặc Apple trước để có email đăng nhập.</p>
        : (
          <form onSubmit={save} className="flex flex-col gap-4">
            {emails.length > 1 ? (
              <label className="flex flex-col gap-2">
                <span className="text-[15px] font-semibold">Email đăng nhập</span>
                <select className="select h-11 w-full rounded-[14px] border-line text-base" value={values.email}
                  onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}>
                  {emails.map((email) => <option key={email} value={email}>{email}</option>)}
                </select>
              </label>
            ) : (
              <p className="text-sm">Đăng nhập bằng {loginNames(security, values.email)}</p>
            )}
            <label className="flex flex-col gap-2">
              <span className="text-[15px] font-semibold">Mật khẩu mới (ít nhất 8 ký tự)</span>
              <input type="password" required minLength={8} autoComplete="new-password" className={FIELD}
                value={values.password} onChange={(e) => setValues((v) => ({ ...v, password: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-[15px] font-semibold">Nhập lại mật khẩu</span>
              <input type="password" required autoComplete="new-password" className={FIELD}
                value={values.password_confirmation} onChange={(e) => setValues((v) => ({ ...v, password_confirmation: e.target.value }))} />
            </label>
            {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
            <button type="submit" className="btn btn-primary h-[54px] w-full rounded-2xl border-0 text-base font-bold" disabled={busy}>
              {busy && <span className="loading loading-spinner loading-sm" />} Lưu mật khẩu
            </button>
          </form>
        )}
    </Sheet>
  );
}

function DisconnectSheet({ provider, security, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const label = provider && PROVIDER_LABELS[provider];
  const info = provider && security.providers[provider];
  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm(provider);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open={!!provider} onClose={onClose} title={`Ngắt kết nối ${label || ''}?`}>
      {provider && (
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <ProviderTile provider={provider} size="size-10" />
            <p className="text-[15px]">
              Bạn sẽ không đăng nhập bằng <b>{info.email ? maskEmail(info.email) : `${label}${info.name ? ` (${info.name})` : ''}`}</b> được
              nữa. Bạn vẫn đăng nhập bằng <b>{remainingNames(security, provider)}</b>. Dữ liệu không bị ảnh hưởng.
            </p>
          </div>
          <button type="button" className="btn btn-error h-[54px] w-full rounded-2xl border-0 text-base font-bold text-white" disabled={busy} onClick={confirm}>
            {busy && <span className="loading loading-spinner loading-sm" />} Ngắt kết nối
          </button>
          <button type="button" className="btn btn-ghost h-11 rounded-2xl" onClick={onClose}>Huỷ</button>
        </div>
      )}
    </Sheet>
  );
}

// Settings › Cách đăng nhập (Zalo, Google, Apple: connect or disconnect) and Mật khẩu.
export default function SignInSettings() {
  const { api, native } = useApp();
  const { search } = useLocation();
  const ref = useRef(null);
  const [security, setSecurity] = useState(null);
  const [providers, setProviders] = useState(null);
  const [status, setStatus] = useState(() => {
    // Back from the website's Zalo trip (Auth::ZaloController).
    const params = new URLSearchParams(search);
    if (params.get('linked')) return { ok: true, text: `Đã kết nối ${PROVIDER_LABELS[params.get('linked')] || ''}` };
    if (params.get('error')) return { ok: false, text: params.get('error') };
    return null;
  });
  const [disconnecting, setDisconnecting] = useState(null);
  const [passwordOpen, setPasswordOpen] = useState(false);

  const load = useCallback(() => api.get('/account/security').then(setSecurity), [api]);
  useEffect(() => {
    load().catch(() => {});
    api.get('/auth/providers').then(setProviders).catch(() => {});
  }, [api, load]);
  // Coming back from Zalo lands on this page: show the result where it happened.
  useEffect(() => {
    if (status && security) ref.current?.scrollIntoView({ block: 'center' });
  }, [!!security]); // eslint-disable-line react-hooks/exhaustive-deps

  const connected = async (_data, provider) => {
    await load();
    setStatus({ ok: true, text: `Đã kết nối ${PROVIDER_LABELS[provider]}` });
  };
  const signIn = useProviderSignIn({ providers, native, intent: 'link', token: native?.token, onDone: connected });

  const disconnect = async (provider) => {
    try {
      await api.del(`/auth/${provider}`);
      await load();
      setStatus({ ok: true, text: `Đã ngắt kết nối ${PROVIDER_LABELS[provider]}` });
    } catch (e) {
      setStatus({ ok: false, text: e.message });
    }
    setDisconnecting(null);
  };

  const removePassword = async () => {
    const phone = security.phone ? ' Đăng nhập bằng số điện thoại cũng sẽ không dùng được nữa.' : '';
    if (!window.confirm(`Bỏ mật khẩu? Bạn sẽ chỉ đăng nhập bằng tài khoản đã kết nối.${phone}`)) return;
    setStatus(null);
    try {
      setSecurity(await api.del('/account/password'));
      setStatus({ ok: true, text: 'Đã bỏ mật khẩu' });
    } catch (e) {
      setStatus({ ok: false, text: e.message });
    }
  };

  if (!security || !providers) return null;

  const rows = PROVIDERS.filter((p) => security.providers[p].connected || providerAvailable(providers, p, native));
  const canRemove = (method) => security.sign_in_methods.some((m) => m !== method);
  const onlyOne = security.sign_in_methods.length === 1 && PROVIDER_LABELS[security.sign_in_methods[0]] ? security.sign_in_methods[0] : null;
  const toConnect = rows.filter((p) => !security.providers[p].connected).map((p) => PROVIDER_LABELS[p]);

  const connectButton = (provider) => {
    const button = (
      <span className={`${SMALL} border-[#17804f] bg-base-100 text-[#17804f]`} aria-hidden={provider === 'google' && !native ? true : undefined}>
        {signIn.busy === provider && <span className="loading loading-spinner loading-xs" />} Kết nối
      </span>
    );
    // Google's own (invisible) button takes the tap on the website; see GoogleWebButton.
    if (provider === 'google' && !native) {
      return <GoogleWebButton clientId={providers.google.web_client_id} onToken={signIn.googleToken} cover>{button}</GoogleWebButton>;
    }
    return (
      <button type="button" className="contents" disabled={!!signIn.busy} onClick={() => signIn.start(provider)} aria-label={`Kết nối ${PROVIDER_LABELS[provider]}`}>
        {button}
      </button>
    );
  };

  return (
    <div ref={ref}>
      <SectionTitle>Cách đăng nhập</SectionTitle>
      {status && <p role="status" className={`px-1 pb-2 text-sm font-medium ${status.ok ? 'text-brand-text' : 'text-error'}`}>{status.text}</p>}
      {rows.length > 0 ? (
        <Group>
          {rows.map((provider) => {
            const info = security.providers[provider];
            return (
              <div key={provider} className="flex min-h-[68px] items-center gap-3 py-3">
                <ProviderTile provider={provider} />
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-bold">{PROVIDER_LABELS[provider]}</span>
                  {info.connected ? (
                    <span className="flex items-center gap-1 truncate text-xs text-muted">
                      <Icon name="check" className="size-3.5 shrink-0 text-[#17804f]" /> {accountName(info)}
                    </span>
                  ) : <span className="block text-xs text-muted">Chưa kết nối</span>}
                </span>
                {info.connected ? (
                  <button type="button" className={`${SMALL} border-line bg-base-100 disabled:border-transparent disabled:bg-base-200 disabled:text-muted`}
                    disabled={!canRemove(provider)} onClick={() => setDisconnecting(provider)}>
                    Ngắt
                  </button>
                ) : connectButton(provider)}
              </div>
            );
          })}
        </Group>
      ) : (
        <p className="px-1 text-sm text-muted">Chưa bật đăng nhập bằng Zalo, Google hay Apple trên máy chủ.</p>
      )}
      {signIn.error && <p role="alert" className="px-1 pt-2 text-sm font-medium text-error">{signIn.error}</p>}
      {onlyOne && (
        <p className="px-1 pt-2 text-xs text-muted">
          {PROVIDER_LABELS[onlyOne]} đang là cách đăng nhập duy nhất nên chưa ngắt được.
          {toConnect.length > 0 ? ` Kết nối thêm ${toConnect.join(' hoặc ')} để có thể ngắt ${PROVIDER_LABELS[onlyOne]}.` : ' Đặt mật khẩu hoặc kết nối tài khoản khác để có thể ngắt.'}
        </p>
      )}
      {security.legacy_phone && (
        <p className="px-1 pt-2 text-xs text-muted">
          Bạn đang đăng nhập bằng số điện thoại {security.phone}.{toConnect.length > 0 && ` Kết nối ${toConnect.join(' hoặc ')} để đăng nhập một chạm;`} số điện thoại và mật khẩu vẫn dùng được như cũ.
        </p>
      )}

      <SectionTitle>Mật khẩu</SectionTitle>
      <Group>
        <div className="flex min-h-14 items-center justify-between gap-3 py-2">
          <span className="min-w-0">
            <span className="block text-[15px] font-semibold">{security.has_password ? 'Đã đặt mật khẩu' : 'Chưa đặt mật khẩu'}</span>
            <span className="block text-xs text-muted">
              {security.has_password ? `Đăng nhập bằng ${loginNames(security)}` : 'Không bắt buộc. Dùng để đăng nhập bằng email.'}
            </span>
          </span>
          <button type="button" className={`${SMALL} border-line bg-base-100`} onClick={() => setPasswordOpen(true)}>
            {security.has_password ? 'Đổi' : 'Đặt'}
          </button>
        </div>
        {/* Removing it also ends phone login, so only while a provider is connected. */}
        {security.has_password && security.sign_in_methods.some((m) => PROVIDER_LABELS[m]) && (
          <button type="button" className="min-h-12 w-full text-left text-sm font-medium text-error" onClick={removePassword}>Bỏ mật khẩu</button>
        )}
      </Group>

      <DisconnectSheet provider={disconnecting} security={security} onClose={() => setDisconnecting(null)} onConfirm={disconnect} />
      <PasswordSheet open={passwordOpen} security={security} onClose={() => setPasswordOpen(false)}
        onSaved={(data) => { setSecurity(data); setPasswordOpen(false); setStatus({ ok: true, text: 'Đã lưu mật khẩu' }); }} />
    </div>
  );
}
